import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import ResumeItem from '@/common/components/ResumeItem'
import useUserPermissions from '@/common/hooks/useUserPermissions'
import { formatDateTime, formatUnit } from '@/common/utils/formatters'
import AuctionProposal from '@/features/maintenanceWorkflow/pages/Show/features/establishmentChoiceEntries/features/auction/components/AuctionProposal'
import AuctionEntryStatus from '@/features/maintenanceWorkflow/pages/Show/features/establishmentChoiceEntries/features/auction/components/status/AuctionEntryStatus'
import { AUCTION_VIEW_PERMISSIONS } from '@/features/maintenanceWorkflow/pages/Show/features/establishmentChoiceEntries/features/auction/constants/auctionPermissions.constant'
import { maintenanceForwardingsDirectiveEntryKlasses } from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/constants/maintenanceForwardingsDirectiveEntryKlasses.constant'
import { MaintenanceForwardingEstablishmentChoiceEntryKlass } from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/types/establishmentChoiceEntries/establishmentChoiceEntry.type'
import { MaintenanceForwardingsDirectiveEntryKlass } from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/constants/maintenanceForwardingsDirectiveEntryKlasses.type'
import { MaintenanceForwardingDirectiveEntryExecutionEntryData } from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/types/maintenanceForwarding.type'
import { useForwardingTabContext } from '@/features/maintenanceWorkflow/pages/Show/hooks/forwardings/useForwardingTabContext'
import { ChevronRightIcon } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

const MaintenanceWorkflowForwardingEstablishmentChoiceEntrySectionAuctionEntry = () => {
  const { forwarding, directiveEntryKlass, noActions } = useForwardingTabContext<
    MaintenanceForwardingsDirectiveEntryKlass,
    MaintenanceForwardingEstablishmentChoiceEntryKlass.AuctionEntry
  >()

  const navigate = useNavigate()

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()

  const hasAuctionViewPermissions =
    isUserPermissionsPending || AUCTION_VIEW_PERMISSIONS.every((permission) => hasUserPermission(permission))

  const auctionEntry = forwarding?.establishment_choice_entry

  const acceptedProposal = auctionEntry?.accepted_proposal

  return (
    <Box className="p-4 flex flex-col gap-4">
      <div className="flex items-center gap-2 justify-between">
        <div>
          <p className="text-lg font-semibold">
            Leilão{' '}
            {directiveEntryKlass && `(${maintenanceForwardingsDirectiveEntryKlasses[directiveEntryKlass]?.label})`}
          </p>

          <AuctionEntryStatus status={auctionEntry?.status} />
        </div>

        {!noActions && (
          <div>
            <Button
              onClick={() => navigate(`${forwarding.id}/auction/${auctionEntry?.id}`)}
              disabled={!hasAuctionViewPermissions}
              tooltipMessage={hasAuctionViewPermissions ? undefined : 'Você não tem permissão'}
            >
              Visualizar

              <ChevronRightIcon strokeWidth={1.5} size={20} />
            </Button>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex gap-3">
          {auctionEntry?.kind && (
            <ResumeItem
              label="Tipo de leilão"
              value="Aberto"
              className="text-xs"
              valueClassName="font-bold text-sm"
            />
          )}

          {auctionEntry?.allowance_radius_in_kilometers && (
            <ResumeItem
              label="Raio de exibição do leilão"
              value={formatUnit(parseInt(auctionEntry?.allowance_radius_in_kilometers), 'kilometers')}
              className="text-xs"
              valueClassName="font-bold text-sm"
            />
          )}
        </div>

        <div className="flex gap-3">
          <ResumeItem
            label="Início do leilão programado"
            value={auctionEntry?.start_at
              ? formatDateTime(new Date(auctionEntry?.start_at))
              : 'Imediato'}
            className="text-xs"
            valueClassName="font-bold text-sm"
          />

          <ResumeItem
            label="Encerramento do leilão programado"
            value={auctionEntry?.finish_at
              ? formatDateTime(new Date(auctionEntry?.finish_at))
              : '--'}
            className="text-xs"
            valueClassName="font-bold text-sm"
          />
        </div>

        {auctionEntry?.status === 'completed' && acceptedProposal && (
          <div className="flex flex-col gap-1">
            <p className="font-light italic text-xs text-white-800">
              Proposta aprovada
            </p>

            <AuctionProposal
              proposal={{
                id: acceptedProposal?.id,
                position: 1,
                establishment_fantasy_name: acceptedProposal?.establishment?.general_information?.fantasy_name || '',
                created_at: '',
                score: acceptedProposal?.score || '',
                contents: acceptedProposal?.proposal_content,
              }}
              directiveEntry={forwarding?.directive_entry as MaintenanceForwardingDirectiveEntryExecutionEntryData}
              directiveEntryType={directiveEntryKlass}
              canAccept={false}
              canDisqualify={false}
              onAccept={() => null}
              onDisqualify={() => null}
              forwardingUpdatedAt={0}
              defaultOpen
            />
          </div>
        )}
      </div>
    </Box>
  )
}

export default MaintenanceWorkflowForwardingEstablishmentChoiceEntrySectionAuctionEntry
